import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { PERMISSIONS } from '@siteflow/shared';
import { PermissionGate } from '@/components/permission-gate';
import { useSiteScope } from '@/components/sites/site-scope';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { messageForError } from '@/lib/api-client';
import { siteKeys, sitesApi } from '@/lib/api/sites';
import { useOrgPermissions } from '@/lib/permissions';

const settingsSchema = z.object({
  name: z.string().min(1, 'A site needs a name').max(200),
  description: z.string().max(1000),
  defaultLocale: z.string().min(2, 'At least two characters').max(20),
});
type SettingsValues = z.infer<typeof settingsSchema>;

function SiteSettings() {
  const { orgId, siteId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { site } = useSiteScope();
  const { has } = useOrgPermissions(orgId);
  const canManage = has(PERMISSIONS.SITE_CONFIG_MANAGE);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const form = useForm<SettingsValues>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      name: site.name,
      description: site.description ?? '',
      defaultLocale: site.defaultLocale,
    },
  });

  useEffect(() => {
    form.reset({
      name: site.name,
      description: site.description ?? '',
      defaultLocale: site.defaultLocale,
    });
  }, [form, site]);

  const updateMutation = useMutation({
    mutationFn: (values: SettingsValues) =>
      sitesApi.update(orgId, siteId, {
        name: values.name,
        description: values.description.trim() || null,
        defaultLocale: values.defaultLocale,
      }),
    onSuccess: () => {
      toast.success('Site settings saved');
      void queryClient.invalidateQueries({ queryKey: siteKeys.all(orgId) });
    },
    onError: (error) => toast.error(messageForError(error, 'Those settings could not be saved.')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => sitesApi.remove(orgId, siteId),
    onSuccess: async () => {
      toast.success(`${site.name} deleted`);
      await queryClient.invalidateQueries({ queryKey: siteKeys.all(orgId) });
      void navigate({ to: '/app/$orgId/sites', params: { orgId } });
    },
    onError: (error) => toast.error(messageForError(error, 'That site could not be deleted.')),
  });

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <Card>
        <CardHeader>
          <span className="section-label">Configuration</span>
          <CardTitle>Site settings</CardTitle>
          <CardDescription>
            The name and description are for people working in this dashboard. The default locale
            decides which set of entries the SDK serves when a request names no locale.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              noValidate
              className="flex flex-col gap-4"
              onSubmit={form.handleSubmit((values) => updateMutation.mutate(values))}
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input disabled={!canManage} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea rows={3} disabled={!canManage} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="defaultLocale"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default locale</FormLabel>
                    <FormControl>
                      <Input className="font-mono" disabled={!canManage} {...field} />
                    </FormControl>
                    <FormDescription>
                      Use <code className="font-mono">default</code> for a single-language site, or a
                      tag such as <code className="font-mono">en-GB</code>.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-col gap-1.5">
                <span className="section-label">Slug</span>
                <p className="font-mono text-data text-ink-muted">{site.slug}</p>
                <p className="text-micro text-ink-subtle">
                  Fixed after creation — the SDK and existing links resolve against it.
                </p>
              </div>

              {canManage ? (
                <div>
                  <Button type="submit" disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? 'Saving…' : 'Save settings'}
                  </Button>
                </div>
              ) : (
                <p className="text-data text-ink-muted">
                  You can view these settings. Changing them needs the site configuration
                  permission.
                </p>
              )}
            </form>
          </Form>
        </CardContent>
      </Card>

      <PermissionGate permission={PERMISSIONS.SITE_DELETE}>
        <Card className="border-danger/30">
          <CardHeader>
            <span className="section-label text-danger">Danger zone</span>
            <CardTitle>Delete this site</CardTitle>
            <CardDescription>
              Every environment, entry, version and media reference for{' '}
              <strong className="text-ink">{site.name}</strong> stops being served. Live pages fall
              back to the copy hard-coded in their markup.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="danger"
              onClick={() => {
                setConfirmText('');
                setDeleteOpen(true);
              }}
            >
              Delete site
            </Button>
          </CardContent>
        </Card>
      </PermissionGate>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {site.name}?</DialogTitle>
            <DialogDescription>
              This releases any licence slot the production environment holds and stops the SDK
              serving content to every deployment of this site. Type the site name to confirm.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-2">
            <Label htmlFor="delete-confirm">
              Type <span className="font-mono text-ink">{site.name}</span>
            </Label>
            <Input
              id="delete-confirm"
              value={confirmText}
              autoComplete="off"
              onChange={(event) => setConfirmText(event.target.value)}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={confirmText !== site.name || deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete this site'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/sites/$siteId/settings')({
  component: SiteSettings,
});
